/**
 * PageMetricStrip - Borderless summary metrics (not mini-cards)
 */

import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDeviceType } from "../../../features/leads/hooks/useDeviceType";

const Strip = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up("sm")]: {
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
}));

const MetricCell = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minWidth: 0,
  flex: 1,
  [theme.breakpoints.up("sm")]: {
    alignItems: "flex-start",
  },
}));

/**
 * @param {Object} props
 * @param {Array<{ value: React.ReactNode, label: string, shortLabel?: string, color?: string }>} props.items
 */
const PageMetricStrip = ({ items = [] }) => {
  const { isMobile } = useDeviceType();

  if (!items.length) return null;

  return (
    <Strip data-testid="page-metric-strip">
      {items.map((item, index) => (
        <React.Fragment key={item.label || index}>
          {index > 0 && <Divider orientation="vertical" flexItem />}
          <MetricCell>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                lineHeight: 1.2,
                color: item.color || "text.primary",
              }}
            >
              {item.value}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              {isMobile && item.shortLabel ? item.shortLabel : item.label}
            </Typography>
          </MetricCell>
        </React.Fragment>
      ))}
    </Strip>
  );
};

export default PageMetricStrip;
